import { tr, usePartnerTranslation } from "../../lib/language";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { NavLink, Route, Routes, Navigate } from "react-router-dom";
import { Loader2, Info, CheckCircle2, Pencil } from "lucide-react";
import { parseApiError } from "../../lib/api";
import { cn } from "../../lib/utils";
import { useAuth } from "../../lib/auth";
import TaxEditModal from "../../components/portal/TaxEditModal";
import PaymentMethodEditModal from "../../components/portal/PaymentMethodEditModal";
import OtpGateModal from "../../components/portal/OtpGateModal";
import PaymentsTab from "../../components/portal/PaymentsTab";
import BookingsForPayoutTab from "../../components/portal/BookingsForPayoutTab";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../components/ui/tooltip";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ================================================================
   Sub-tab navigation
   ================================================================ */
const TABS = [
  { to: "/finance/bookings", get label() { return tr("Summary"); } },
  { to: "/finance/payments", get label() { return tr("Payouts"); } },
  { to: "/finance/details", get label() { return tr("Finance details"); } },
];

const TabsBar = () => {
  usePartnerTranslation();
  return <nav data-testid="finance-tabs" className="flex flex-wrap gap-x-6 gap-y-1 border-b border-[#EAECEF]">
    {TABS.map(t => <NavLink key={t.to} to={t.to} data-testid={`finance-tab-${t.to.split("/").pop()}`} className={({
      isActive
    }) => cn("-mb-px whitespace-nowrap border-b-2 py-3 text-sm font-semibold transition-colors", isActive ? "border-[#FF5757] text-[#0F1115]" : "border-transparent text-[#6B7280] hover:text-[#0F1115]")}>
        {t.label}
      </NavLink>)}
  </nav>;
};

/* ================================================================
   Placeholder tabs
   ================================================================ */
const PaymentsPlaceholder = PaymentsTab;

/* ================================================================
   Finance details tab — Tax details + Payment method
   ================================================================ */

const DetailRow = ({
  label,
  value,
  info,
  testid
}) => {
  usePartnerTranslation();
  return <div data-testid={testid} className="flex flex-col gap-1 py-3 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
    <dt className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F1115]">
      {label}
      {info && <TooltipProvider delayDuration={100}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="h-3.5 w-3.5 text-[#9BA1AB]" strokeWidth={1.8} tabIndex={0} />
            </TooltipTrigger>
            <TooltipContent className="max-w-[260px] border-[#EAECEF] bg-white text-[#0F1115]" sideOffset={6}>
              <p className="text-xs">{info}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>}
    </dt>
    <dd className="text-sm text-[#0F1115] sm:max-w-[60%] sm:text-end">
      {value || <span className="text-[#9BA1AB]">—</span>}
    </dd>
  </div>;
};

const Card = ({
  title,
  children,
  onEdit,
  actionLabel = "Edit",
  actionTestid
}) => {
  usePartnerTranslation();
  return <section className="rounded-2xl border border-[#EAECEF] bg-white">
    <header className="flex items-start justify-between gap-4 px-5 py-4 sm:px-7">
      <h3 className="text-lg font-extrabold tracking-tight text-[#0F1115] sm:text-xl">
        {title}
      </h3>
      {onEdit && <button type="button" onClick={onEdit} data-testid={actionTestid} className="inline-flex h-9 items-center gap-2 rounded-md border border-[#2476FF] px-4 text-sm font-semibold text-[#2476FF] transition-colors hover:bg-[#2476FF] hover:text-white">
          <Pencil className="h-3.5 w-3.5" />
          {actionLabel}
        </button>}
    </header>
    <div className="border-t border-[#EFE4E4] px-5 py-2 sm:px-7">{children}</div>
  </section>;
};

const FinanceDetailsTab = () => {
  usePartnerTranslation();
  const { user, role } = useAuth();
  const loginEmail = user?.email || "";
  const canEditFinance = Boolean(user?.is_owner) || role === "administration";
  const [loading, setLoading] = useState(true);
  const [finance, setFinance] = useState(null);

  // Modal state
  const [taxOpen, setTaxOpen] = useState(false);
  const [otpOpen, setOtpOpen] = useState(false);
  const [verifiedOtp, setVerifiedOtp] = useState(""); // valid for one edit
  const [paymentOpen, setPaymentOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/finance/me`);
        if (!cancelled) setFinance(res.data);
      } catch (err) {
        const { message } = parseApiError(err, tr("Couldn't load finance details."));
        if (process.env.NODE_ENV !== "production") {
          console.warn("[finance] load failed", message, err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const onTaxSaved = (tax) => {
    setFinance((prev) => ({ ...prev, tax }));
    setTaxOpen(false);
  };

  const onPaymentSaved = (pm) => {
    setFinance((prev) => ({ ...prev, payment_method: pm }));
    setPaymentOpen(false);
    setVerifiedOtp("");
  };

  const onOtpVerified = (code) => {
    setVerifiedOtp(code);
    setOtpOpen(false);
    setPaymentOpen(true);
  };

  if (loading) {
    return (
      <section className="mt-6 flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-[#FF5757]" />
      </section>
    );
  }

  const tax = finance?.tax || {};
  const pm = finance?.payment_method || {};

  return (
    <section data-testid="tab-finance-details" className="mt-6 space-y-6">
      {/* Tax details */}
      <Card
        title={tr("Tax details")}
        onEdit={canEditFinance ? () => setTaxOpen(true) : undefined}
        actionTestid="tax-edit-trigger"
      >
        <dl className="divide-y divide-[#F0EFEF]">
          <DetailRow
            testid="tax-row-vat-number"
            label={tr("Value Added Tax (VAT) Number")}
            info={tr("Enter your VAT number if you're VAT-registered. We include this on invoices to customers.")}
            value={tax.has_vat ? tax.vat_number || "—" : ""}
          />
          <DetailRow
            testid="tax-row-vat-country"
            label={tr("Country of issue")}
            value={tax.has_vat ? tax.vat_country : ""}
          />
          <DetailRow
            testid="tax-row-utr-number"
            label={tr("Unique Taxpayer Reference (UTR)")}
            info={tr("Your tax authority's unique reference for your business. Required for cross-border payouts.")}
            value={tax.utr_number}
          />
          <DetailRow
            testid="tax-row-utr-country"
            label={tr("Country of issue")}
            value={tax.utr_country}
          />
        </dl>
      </Card>

      {/* Payment method */}
      <Card
        title={tr("Payment method")}
        onEdit={canEditFinance
          ? () => {
              if (!loginEmail) return;
              setOtpOpen(true);
            }
          : undefined}
        actionTestid="pm-edit-trigger"
      >
        <dl className="divide-y divide-[#F0EFEF]">
          <DetailRow
            testid="pm-row-method"
            label={tr("Payment method")}
            value={pm.method === "paypal" ? "PayPal" : "Bank transfer"}
          />

          {pm.method === "paypal" ? (
            <DetailRow
              testid="pm-row-paypal-email"
              label={tr("PayPal email")}
              value={pm.paypal_email}
            />
          ) : (
            <>
              <DetailRow testid="pm-row-iban" label="IBAN" value={pm.iban} />
              <DetailRow testid="pm-row-swift" label="SWIFT / BIC" value={pm.swift} />
              <DetailRow
                testid="pm-row-account-type"
                label={tr("Account type")}
                value={pm.account_type}
              />
              <DetailRow
                testid="pm-row-beneficiary"
                label={tr("Beneficiary name")}
                value={pm.beneficiary_name}
              />
              <DetailRow
                testid="pm-row-bank-city"
                label={tr("Bank city")}
                value={pm.bank_city}
              />
              <DetailRow
                testid="pm-row-bank-name"
                label={tr("Bank name")}
                value={pm.bank_name}
              />
              <DetailRow
                testid="pm-row-bank-address"
                label={tr("Bank address")}
                value={pm.bank_address}
              />
              <DetailRow
                testid="pm-row-bank-zip"
                label={tr("Bank zip")}
                value={pm.bank_zip}
              />
            </>
          )}
        </dl>
      </Card>

      {/* ---------------- Payment settings (payout currency) ---------------- */}
      <PaymentSettingsCard currency={finance?.payout_currency} />

      {/* Modals */}
      {canEditFinance && (
        <TaxEditModal
          open={taxOpen}
          initial={tax}
          onClose={() => setTaxOpen(false)}
          onSaved={onTaxSaved}
        />
      )}

      {canEditFinance && (
        <OtpGateModal
          open={otpOpen}
          email={loginEmail}
          title={tr("Verify it's you")}
          description={tr("To keep your payouts safe, confirm a 6-digit code we sent to your email before editing your payment method.")}
          onClose={() => setOtpOpen(false)}
          onVerified={onOtpVerified}
        />
      )}

      {canEditFinance && (
        <PaymentMethodEditModal
          open={paymentOpen}
          initial={pm}
          otpEmail={loginEmail}
          otpCode={verifiedOtp}
          onClose={() => {
            setPaymentOpen(false);
            setVerifiedOtp("");
          }}
          onSaved={onPaymentSaved}
        />
      )}
    </section>
  );
};

/* ================================================================
   Payment settings card — read-only payout currency display.
   The currency is derived from the supplier's signup choice and
   cannot be changed here.
   ================================================================ */
const PaymentSettingsCard = ({
  currency
}) => {
  usePartnerTranslation();
  return <div data-testid="payment-settings-card" className="space-y-4 rounded-2xl border border-[#EAECEF] bg-white p-6">
    <div className="flex items-center justify-between gap-3">
      <h2 className="text-lg font-extrabold tracking-tight text-[#0F1115]">{tr("Currency")}</h2>
    </div>
    <div className="grid gap-4 md:grid-cols-[1fr_220px] md:items-center">
      <div>
        <p className="text-sm font-semibold text-[#0F1115]">{tr("Payout currency")}</p>
        <p className="mt-1 text-xs text-[#6B7280]">{tr("Currency you will be paid in by Voocly.")}</p>
      </div>
      <div data-testid="payout-currency-display" className="flex h-10 w-full items-center justify-center rounded-md border border-[#E6E8EC] bg-[#FAFAFB] text-sm font-extrabold tracking-wide text-[#0F1115]">
        {currency || "—"}
      </div>
    </div>
  </div>;
};

/* ================================================================
   Page shell
   ================================================================ */
export default function FinancePage() {
  usePartnerTranslation();
  return (
    <main
      data-testid="finance-page"
      className="mx-auto w-full max-w-[1100px] flex-1 px-4 py-10 sm:px-6 md:px-10"
    >
      <header>
        <h1 className="text-3xl font-extrabold tracking-tight text-[#0F1115] md:text-4xl">{tr("Finance")}</h1>
        <p className="mt-2 max-w-xl text-sm text-[#6B7280]">{tr("Track your earnings, view payout history, and manage tax & payment details on file with Voocly.")}</p>
      </header>

      <div className="mt-8">
        <TabsBar />
      </div>

      <Routes>
        <Route index element={<Navigate to="bookings" replace />} />
        <Route path="bookings" element={<BookingsForPayoutTab />} />
        <Route path="payments" element={<PaymentsPlaceholder />} />
        <Route path="details" element={<FinanceDetailsTab />} />
      </Routes>
    </main>
  );
}
